import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Sparkles, X, ImagePlus, Loader2, Pencil } from "lucide-react";

export default function RecommendationsAdmin() {
  const [cats, setCats] = useState([]);
  const [subs, setSubs] = useState([]);
  const [products, setProducts] = useState([]);
  const [catName, setCatName] = useState("");
  const [subInputs, setSubInputs] = useState({}); // catId -> name

  // product form
  const [pCat, setPCat] = useState("");
  const [pSub, setPSub] = useState("");
  const [pTitle, setPTitle] = useState("");
  const [pDesc, setPDesc] = useState("");
  const [pLink, setPLink] = useState("");
  const [pImages, setPImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const loadAll = () => {
    api.get("/rec/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/rec/subcategories").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/rec/products").then((r) => setProducts(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const createCat = async (e) => {
    e.preventDefault();
    if (!catName.trim()) return;
    try {
      const r = await api.post("/rec/categories", { name: catName.trim() });
      setCats((c) => [...c, r.data]);
      setCatName("");
      toast.success("Categoria creata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const deleteCat = async (id) => {
    if (!window.confirm("Eliminare la categoria con le sue sottocategorie e prodotti?")) return;
    await api.delete(`/rec/categories/${id}`);
    setCats((c) => c.filter((x) => x.id !== id));
    setSubs((s) => s.filter((x) => x.category_id !== id));
    setProducts((p) => p.filter((x) => x.category_id !== id));
    toast.success("Categoria eliminata");
  };

  const createSub = async (catId) => {
    const name = (subInputs[catId] || "").trim();
    if (!name) return;
    try {
      const r = await api.post("/rec/subcategories", { category_id: catId, name });
      setSubs((s) => [...s, r.data]);
      setSubInputs((m) => ({ ...m, [catId]: "" }));
      toast.success("Sottocategoria creata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const deleteSub = async (id) => {
    if (!window.confirm("Eliminare la sottocategoria e i suoi prodotti?")) return;
    await api.delete(`/rec/subcategories/${id}`);
    setSubs((s) => s.filter((x) => x.id !== id));
    setProducts((p) => p.filter((x) => x.subcategory_id !== id));
    toast.success("Sottocategoria eliminata");
  };

  const uploadImages = async (files) => {
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPImages((imgs) => [...imgs, r.data.url]);
      }
    } catch {
      toast.error("Errore caricamento immagine");
    } finally {
      setUploading(false);
    }
  };

  const createProduct = async (e) => {
    e.preventDefault();
    if (!pCat) return toast.error("Scegli una categoria");
    if (!pSub) return toast.error("Scegli una sottocategoria");
    if (!pTitle.trim() || !pLink.trim()) return toast.error("Titolo e link obbligatori");
    setSaving(true);
    const payload = {
      category_id: pCat, subcategory_id: pSub, title: pTitle.trim(), description: pDesc.trim(), link: pLink.trim(), images: pImages,
    };
    try {
      if (editingId) {
        const r = await api.put(`/rec/products/${editingId}`, payload);
        setProducts((p) => p.map((x) => (x.id === editingId ? r.data : x)));
        toast.success("Recensione aggiornata");
      } else {
        const r = await api.post("/rec/products", payload);
        setProducts((p) => [...p, r.data]);
        toast.success("Prodotto aggiunto");
      }
      resetForm();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setPCat(""); setPSub(""); setPTitle(""); setPDesc(""); setPLink(""); setPImages([]);
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setPCat(p.category_id || "");
    setPSub(p.subcategory_id || "");
    setPTitle(p.title || "");
    setPDesc(p.description || "");
    setPLink(p.link || "");
    setPImages(p.images || []);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteProduct = async (id) => {
    await api.delete(`/rec/products/${id}`);
    setProducts((p) => p.filter((x) => x.id !== id));
    toast.success("Prodotto eliminato");
  };

  const catName_ = (id) => cats.find((c) => c.id === id)?.name || "—";
  const subName_ = (id) => subs.find((s) => s.id === id)?.name || "—";
  const subsOf = (catId) => subs.filter((s) => s.category_id === catId);
  const formSubs = useMemo(() => subs.filter((s) => s.category_id === pCat), [subs, pCat]);

  return (
    <div className="relative z-10 min-h-screen bg-[#F9F8F6]">
      <div className="border-b border-[#E7E5E4] bg-[#F2F0EB]">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 label-ui text-[#57534E] hover:text-[#9F583F]"><ArrowLeft size={15} /> Torna</Link>
          <Link to="/le-giraffe-consigliano" target="_blank" className="inline-flex items-center gap-2 label-ui text-[#57534E] hover:text-[#9F583F]"><Sparkles size={15} /> Vedi la sezione</Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <p className="label-ui text-[#9F583F] mb-2">Le Giraffe Consigliano</p>
        <h1 className="font-display text-4xl tracking-tight text-[#1C1917] mb-10">Gestione consigli</h1>

        {/* Categories + subcategories */}
        <div className="border border-[#E7E5E4] bg-[#F2F0EB] p-6 mb-10">
          <p className="font-display text-2xl text-[#1C1917] mb-4">Categorie e sottocategorie</p>
          <form onSubmit={createCat} className="flex gap-3 mb-6">
            <input data-testid="rec-cat-name" value={catName} onChange={(e) => setCatName(e.target.value)} placeholder="Nuova categoria — es. Giardinaggio" className="flex-1 font-ui text-sm border border-[#E7E5E4] bg-white px-3 py-2 focus:outline-none focus:border-[#9F583F]" />
            <button type="submit" data-testid="rec-cat-create" className="inline-flex items-center gap-2 bg-[#1C1917] text-white label-ui px-4 py-2 hover:bg-[#9F583F] transition-colors"><Plus size={14} /> Aggiungi</button>
          </form>

          <div className="space-y-4">
            {cats.map((c) => (
              <div key={c.id} data-testid={`rec-cat-${c.name}`} className="bg-white border border-[#E7E5E4] p-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <span className="font-display text-lg text-[#1C1917]">{c.name}</span>
                  <button onClick={() => deleteCat(c.id)} data-testid={`rec-cat-delete-${c.name}`} className="text-[#A8A29E] hover:text-red-600" title="Elimina categoria"><Trash2 size={15} /></button>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {subsOf(c.id).map((s) => (
                    <span key={s.id} data-testid={`rec-sub-${s.name}`} className="inline-flex items-center gap-2 bg-[#F2F0EB] border border-[#E7E5E4] px-3 py-1.5 label-ui text-[#1C1917]">
                      {s.name}
                      <button onClick={() => deleteSub(s.id)} data-testid={`rec-sub-delete-${s.name}`} className="text-[#A8A29E] hover:text-red-600"><X size={13} /></button>
                    </span>
                  ))}
                  {subsOf(c.id).length === 0 && <span className="font-body italic text-[#A8A29E] text-sm">Nessuna sottocategoria.</span>}
                </div>
                <div className="flex gap-2">
                  <input
                    value={subInputs[c.id] || ""}
                    onChange={(e) => setSubInputs((m) => ({ ...m, [c.id]: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createSub(c.id); } }}
                    placeholder="Nuova sottocategoria — es. Tagliaerba"
                    data-testid={`rec-sub-input-${c.name}`}
                    className="flex-1 font-ui text-sm border border-[#E7E5E4] bg-white px-3 py-1.5 focus:outline-none focus:border-[#9F583F]"
                  />
                  <button onClick={() => createSub(c.id)} data-testid={`rec-sub-create-${c.name}`} className="inline-flex items-center gap-1 bg-[#9F583F] text-white label-ui px-3 py-1.5 hover:bg-[#1C1917] transition-colors"><Plus size={13} /> Aggiungi</button>
                </div>
              </div>
            ))}
            {cats.length === 0 && <span className="font-body italic text-[#57534E]">Nessuna categoria ancora.</span>}
          </div>
        </div>

        {/* New product */}
        <form onSubmit={createProduct} className="border border-[#E7E5E4] bg-[#F2F0EB] p-6 mb-12" data-testid="rec-product-form">
          <div className="flex items-center justify-between mb-5">
            <p className="font-display text-2xl text-[#1C1917]">{editingId ? "Modifica recensione" : "Nuovo prodotto"}</p>
            {editingId && (
              <button type="button" onClick={resetForm} data-testid="rec-prod-cancel-edit" className="inline-flex items-center gap-1 label-ui text-[#57534E] hover:text-[#9F583F]"><X size={14} /> Annulla modifica</button>
            )}
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="label-ui text-[#57534E] block mb-2">Categoria</label>
              <select data-testid="rec-prod-cat" value={pCat} onChange={(e) => { setPCat(e.target.value); setPSub(""); }} className="w-full font-ui text-sm border border-[#E7E5E4] bg-white px-3 py-2 focus:outline-none focus:border-[#9F583F]">
                <option value="">— Scegli —</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-ui text-[#57534E] block mb-2">Sottocategoria</label>
              <select data-testid="rec-prod-sub" value={pSub} onChange={(e) => setPSub(e.target.value)} disabled={!pCat} className="w-full font-ui text-sm border border-[#E7E5E4] bg-white px-3 py-2 focus:outline-none focus:border-[#9F583F] disabled:opacity-50">
                <option value="">{pCat ? "— Scegli —" : "Scegli prima una categoria"}</option>
                {formSubs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              {pCat && formSubs.length === 0 && <p className="label-ui text-red-600 mt-1">Crea prima una sottocategoria per questa categoria.</p>}
            </div>
          </div>
          <div className="mt-5">
            <label className="label-ui text-[#57534E] block mb-2">Titolo</label>
            <input data-testid="rec-prod-title" value={pTitle} onChange={(e) => setPTitle(e.target.value)} placeholder="Nome del prodotto" className="w-full font-ui text-sm border border-[#E7E5E4] bg-white px-3 py-2 focus:outline-none focus:border-[#9F583F]" />
          </div>
          <div className="mt-5">
            <label className="label-ui text-[#57534E] block mb-2">Recensione / testo</label>
            <textarea data-testid="rec-prod-desc" value={pDesc} onChange={(e) => setPDesc(e.target.value)} rows={4} placeholder="Descrivi il prodotto e perché lo consigli…" className="w-full font-ui text-sm border border-[#E7E5E4] bg-white px-3 py-2 focus:outline-none focus:border-[#9F583F]" />
          </div>
          <div className="mt-5">
            <label className="label-ui text-[#57534E] block mb-2">Link di riferimento (Amazon affiliate)</label>
            <input data-testid="rec-prod-link" value={pLink} onChange={(e) => setPLink(e.target.value)} placeholder="https://www.amazon.it/…" className="w-full font-ui text-sm border border-[#E7E5E4] bg-white px-3 py-2 focus:outline-none focus:border-[#9F583F]" />
          </div>
          <div className="mt-5">
            <label className="label-ui text-[#57534E] block mb-2">Immagini</label>
            <div className="flex flex-wrap gap-3 items-center">
              {pImages.map((im, i) => (
                <div key={i} className="relative w-20 h-20 border border-[#E7E5E4] bg-white flex items-center justify-center p-1">
                  <img src={mediaUrl(im)} alt="" className="max-w-full max-h-full object-contain" />
                  <button type="button" onClick={() => setPImages((x) => x.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 bg-white border border-[#E7E5E4] w-6 h-6 flex items-center justify-center text-[#1C1917] hover:text-red-600"><X size={12} /></button>
                </div>
              ))}
              <label className="w-20 h-20 border border-dashed border-[#C9C2BA] flex items-center justify-center cursor-pointer text-[#9F583F] hover:border-[#9F583F]" data-testid="rec-prod-upload">
                {uploading ? <Loader2 size={20} className="animate-spin" /> : <ImagePlus size={22} strokeWidth={1.5} />}
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { uploadImages(Array.from(e.target.files)); e.target.value = ""; }} />
              </label>
            </div>
          </div>
          <button type="submit" disabled={saving} data-testid="rec-prod-create" className="mt-6 inline-flex items-center gap-2 bg-[#9F583F] text-white label-ui px-6 py-3 hover:bg-[#1C1917] transition-colors disabled:opacity-60">
            <Plus size={15} /> {saving ? "Salvataggio…" : editingId ? "Salva modifiche" : "Aggiungi prodotto"}
          </button>
        </form>

        {/* Products list */}
        <p className="font-display text-2xl text-[#1C1917] mb-5">Prodotti ({products.length})</p>
        <div className="space-y-3" data-testid="rec-products-list">
          {products.map((p) => (
            <div key={p.id} className="border border-[#E7E5E4] bg-[#F2F0EB] p-4 flex items-center gap-4" data-testid={`rec-admin-product-${p.id}`}>
              <div className="w-16 h-16 bg-white border border-[#E7E5E4] shrink-0 overflow-hidden flex items-center justify-center p-1">
                {p.images?.[0] ? <img src={mediaUrl(p.images[0])} alt="" className="max-w-full max-h-full object-contain" /> : null}
              </div>
              <div className="min-w-0 flex-1">
                <span className="label-ui text-[#9F583F]">{catName_(p.category_id)} › {subName_(p.subcategory_id)}</span>
                <p className="font-display text-lg text-[#1C1917] truncate">{p.title}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => startEdit(p)} data-testid={`rec-prod-edit-${p.id}`} className="text-[#A8A29E] hover:text-[#9F583F]" title="Modifica recensione"><Pencil size={16} /></button>
                <button onClick={() => deleteProduct(p.id)} data-testid={`rec-prod-delete-${p.id}`} className="text-[#A8A29E] hover:text-red-600" title="Elimina"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
          {products.length === 0 && <p className="font-body italic text-[#57534E]">Nessun prodotto ancora.</p>}
        </div>
      </div>
    </div>
  );
}
